import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import ListHeader from './ListHeader';
import ListBody from './ListBody';
import ListFooter from './ListFooter';

const List = forwardRef(({
  // 필수 props
  headers,
  onGetData, // 데이터 가져오는 함수
  
  // 선택적 props
  buttons = [],
  className = '',
  renderActions,
  onRowClick,
  
  // 초기값 설정
  initialPage = 1,
  initialRowsPerPage = 20,
  initialSort = '',
  initialDesc = 'desc',
  initialKeyword = ''
}, ref) => {
  // 내부 상태 관리
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [sort, setSort] = useState(initialSort || (headers.find(h => h.value)?.value || ''));
  const [desc, setDesc] = useState(initialDesc);
  const [keyword, setKeyword] = useState(initialKeyword);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]); // 내부에서 관리

  const sortOptions = headers.filter(header => header.value !== '');

  // 선택된 항목 변경 핸들러 (내부 함수만 유지)
  const handleSelectionChange = (newSelectedItems) => {
    setSelectedItems(newSelectedItems);
    // 부모 컴포넌트 알림 코드 제거
  };

  // 데이터 가져오기
  const handleGetData = async (params = {}) => {
    const requestParams = {
      page: params.page !== undefined ? params.page : page,
      rowsPerPage: params.rowsPerPage !== undefined ? params.rowsPerPage : rowsPerPage,
      sort: params.sort !== undefined ? params.sort : sort,
      desc: params.desc !== undefined ? params.desc : desc,
      keyword: params.keyword !== undefined ? params.keyword : keyword,
      ...params // 추가 파라미터들
    };

    try {
      const response = await onGetData(requestParams);
      setTotal(response.total || 0);
      setTotalPages(response.totalPages || 0);
      setItems(response.lists || response.items || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setItems([]);
    }
  };

  // 아이템 리셋 함수
  const resetItems = () => {
    console.log('List resetItems called');
    setItems([]);
    setSelectedItems([]);
    setTotal(0);
    setTotalPages(0);
    setPage(1);
  };

  // 페이지 변경
  const handleMovePage = (pageNum) => {
    if (pageNum <= 0 || pageNum > totalPages) return;
    setPage(pageNum);
  };

  // 정렬 변경
  const handleSortChange = (newSort, currentSort, setSort, setDesc) => {
    if (newSort === currentSort) {
      setDesc((prevDesc) => (prevDesc === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(newSort);
      setDesc('asc');
    }
  };

  // 검색
  const handleSearch = () => {
    if (page === 1) {
      handleGetData();
    } else {
      setPage(1);
    }
  };

  // 전체 선택
  const handleSelectAll = (checked) => {
    if (checked) {
      const newSelectedItems = items.map(item => item.id);
      handleSelectionChange(newSelectedItems);
    } else {
      handleSelectionChange([]);
    }
  };

  // 개별 선택
  const handleItemSelect = (itemId, checked) => {
    let newSelectedItems;
    if (checked) {
      newSelectedItems = [...selectedItems, itemId];
    } else {
      newSelectedItems = selectedItems.filter(id => id !== itemId);
    }
    handleSelectionChange(newSelectedItems);
  };

  // 페이지 변경 시 데이터 새로고침
  useEffect(() => {
    handleGetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, sort, desc]);

  // 외부에서 새로고침할 수 있는 함수 노출
  const refresh = (params = {}) => {
    handleGetData(params);
  };

  // 외부에서 접근할 수 있도록 ref 처리
  useImperativeHandle(ref, () => ({
    refresh,
    resetItems,
    getSelectedItems: () => selectedItems,
    clearSelection: () => handleSelectionChange([])
  }));

  return (
    <div className="list-component">
      <ListHeader
        headers={headers}
        buttons={buttons}
        keyword={keyword} 
        setKeyword={setKeyword}
        handleSearch={handleSearch}
        page={page} 
        setPage={handleMovePage}
        rowsPerPage={rowsPerPage} 
        setRowsPerPage={setRowsPerPage}
        sort={sort} 
        setSort={setSort}
        desc={desc} 
        setDesc={setDesc}
        sortOptions={sortOptions}
        total={total}
        totalPages={totalPages}
      />
      
      <ListBody
        headers={headers}
        items={items}
        selectedItems={selectedItems}
        handleSelectAll={handleSelectAll}
        handleItemSelect={handleItemSelect}
        sort={sort}
        desc={desc}
        handleSortChange={handleSortChange}
        setSort={setSort}
        setDesc={setDesc}
        renderActions={renderActions}
        onRowClick={onRowClick}
        className={className}
      />
      
      <ListFooter
        page={page}
        totalPages={totalPages}
        handlePageChange={handleMovePage}
      />
    </div>
  );
});

export default List;